import { Injectable, ComponentRef } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';

export interface Tab {
  label: string;
  path: string;
  fullPath: string;
  filterData: any;
  params?: any;
  quickFilter?: string | null;
}

interface Stored {
  handle: DetachedRouteHandle;
  scrolls: { idx: number; top: number; left: number }[];
}

@Injectable({ providedIn: 'root' })
export class TabService implements RouteReuseStrategy {
  private storageKey = 'tabs';
  private tabsSubject: BehaviorSubject<Tab[]>;
  private activeTabSubject: BehaviorSubject<number>;
  tabs$: any;
  activeTab$: any;

  // ---- lifecycle streams: subscribe from any child component ----
  private deactivatedSubject = new Subject<void>();
  private activatedSubject = new Subject<void>();
  deactivated$ = this.deactivatedSubject.asObservable();
  activated$ = this.activatedSubject.asObservable();

  // ---- reuse cache ----
  private cache = new Map<string, Stored>();

  private singletonPathPatterns = [
    'receiving-services/asn', 'receiving-services/putaway', 'receiving-services/grn',
    'order-services/picklist', 'order-services/order', 'order-services/manifest',
    'utility/upload', 'ftl-master/quick-code',
    'orders/order-view', 'orders/order-trip', 'orders/end-trip',
  ];

  constructor() {
    const savedTabs = localStorage.getItem(this.storageKey);
    this.tabsSubject = new BehaviorSubject<Tab[]>(savedTabs ? JSON.parse(savedTabs) : []);
    const savedActive = localStorage.getItem(this.storageKey + '_active');
    this.activeTabSubject = new BehaviorSubject<number>(savedActive ? Number(savedActive) : 0);
    this.tabs$ = this.tabsSubject.asObservable();
    this.activeTab$ = this.activeTabSubject.asObservable();
  }

  // =====================  TAB LOGIC (unchanged except closeTab/closeAllTab)  =====================

  private saveTabs() { localStorage.setItem(this.storageKey, JSON.stringify(this.tabsSubject.value)); }
  private saveActiveTab() { localStorage.setItem(this.storageKey + '_active', this.activeTabSubject.value.toString()); }
  get tabs(): Tab[] { return this.tabsSubject.value; }
  get activeTab(): number { return this.activeTabSubject.value; }

  addTab(tab: Tab) {
    const pathWithParams = tab.params
      ? `${tab.path}?${new URLSearchParams(tab.params).toString()}`
      : tab.path;
    tab.fullPath = pathWithParams;

    const existingTab = this.tabs.findIndex(t => {
      const matched = this.singletonPathPatterns.find(p => t?.path?.includes(p) && tab?.path?.includes(p));
      return matched ? true : t.fullPath === pathWithParams;
    });

    if (existingTab === -1) {
      this.tabsSubject.next([...this.tabs, tab]);
      this.setActiveTab(this.tabs.length);
    } else {
      const existing = this.tabs[existingTab];
      const oldFullPath = existing.fullPath;

      // singleton tab now points to a different id/params:
      // update the tab and throw away the old cached page
      if (oldFullPath !== pathWithParams) {
        this.destroyCached(this.keyFromUrl(oldFullPath));
        existing.fullPath = pathWithParams;
        existing.path = tab.path;
        existing.params = tab.params;
      }
      this.setActiveTab(existingTab);
    }
    this.saveTabs();
    this.saveActiveTab();
  }

  setActiveTab(index: number) {
    this.activeTabSubject.next(index);
    this.saveActiveTab();
  }

  updateTab(index: number, tab: Tab) {
    const tabs = [...this.tabs];
    if (tabs.length === index) index = tabs.length - 1;
    if (tabs.length > 0) { tabs[index] = tab; this.tabsSubject.next(tabs); this.saveTabs(); }
  }

  closeTab(index: number) {
    const tabs = [...this.tabs];
    const [removed] = tabs.splice(index, 1);
    this.tabsSubject.next(tabs);
    this.saveTabs();

    if (removed) this.destroyCached(this.keyFromUrl(removed.fullPath || removed.path));

    if (this.activeTab === index) {
      this.setActiveTab(tabs.length > 0 ? (index === 0 ? 0 : index - 1) : 0);
    } else if (this.activeTab > index) {
      this.setActiveTab(this.activeTab - 1);
    }
    this.saveActiveTab();
  }

  closeAllTab() {
    this.tabsSubject.next([]);
    this.saveTabs();
    this.clearAll();
  }

  updateFilterData(index: number, filterData: any) {
    const tabs = [...this.tabs];
    if (tabs.length === index) index = tabs.length - 1;
    if (tabs.length > 0) { tabs[index].filterData = filterData; this.tabsSubject.next(tabs); this.saveTabs(); }
  }

  transformFiltersArrayToObject(filtersArray: any[]): any {
    return filtersArray.reduce((acc, curr) => {
      if (curr.value) acc[curr.key] = curr.value;
      if (curr.dateTimeValue) acc[curr.key] = curr.dateTimeValue;
      return acc;
    }, {});
  }

  activateTabForPath(path: string): number {
    const clean = path.split('?')[0];
    const index = this.tabs.findIndex(t => t.path === clean || t.fullPath.split('?')[0] === clean);
    if (index !== -1) this.setActiveTab(index);
    return index;
  }

  getQuickFilter(index?: number): string | null {
    const i = index ?? this.activeTab;
    if (i < 0 || i >= this.tabs.length) return null;
    return this.tabs[i]?.quickFilter ?? null;
  }

  updateQuickFilter(index: number, quickFilter: string | null): void {
    const tabs = [...this.tabs];
    if (index < 0 || index >= tabs.length) return;
    tabs[index] = { ...tabs[index], quickFilter };
    this.tabsSubject.next(tabs);
    this.saveTabs();
  }

  // =====================  ROUTE REUSE  =====================

  /** One key function for both routes and tabs. Singleton pages are keyed by pattern. */
  /** One key function for both routes and tabs. Every id/query gets its own key. */
  private makeKey(path: string, query: { [k: string]: any }): string {
    const clean = path.replace(/^\//, '');
    const qs = Object.keys(query).sort().map(k => `${k}=${query[k]}`).join('&');
    return qs ? `${clean}?${qs}` : clean;
  }
  
  private keyFromUrl(url: string): string {
    const [path, qs] = url.split('?');
    const query: any = {};
    new URLSearchParams(qs || '').forEach((v, k) => (query[k] = v));
    return this.makeKey(path, query);
  }

  private keyFromRoute(route: ActivatedRouteSnapshot): string {
    const path = route.pathFromRoot
      .map(s => s.url.map(u => u.path).join('/'))
      .filter(Boolean)
      .join('/');
    return this.makeKey(path, route.queryParams);
  }

  private isTabRoute(route: ActivatedRouteSnapshot): boolean {
    return !!route.routeConfig && !!route.component && !route.firstChild;
  }

  /** Only cache pages that still have an open tab (prevents closed tabs from being re-cached) */
  private hasOpenTab(key: string): boolean {
    return this.tabs.some(t => this.keyFromUrl(t.fullPath || t.path) === key);
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this.isTabRoute(route) && this.hasOpenTab(this.keyFromRoute(route));
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (!handle) return;
    this.cache.set(this.keyFromRoute(route), { handle, scrolls: this.captureScroll(handle) });

    const ref = this.ref(handle);
    ref?.instance?.onTabDeactivated?.();
    this.deactivatedSubject.next();          // lets overlays / dialogs close themselves
    ref?.changeDetectorRef.detectChanges();
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return this.isTabRoute(route) && this.cache.has(this.keyFromRoute(route));
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!this.isTabRoute(route)) return null;
    const stored = this.cache.get(this.keyFromRoute(route));
    if (!stored) return null;
    setTimeout(() => {
      this.restoreScroll(stored);
      const ref = this.ref(stored.handle);
      ref?.instance?.onTabActivated?.();
      this.activatedSubject.next();
      ref?.changeDetectorRef.detectChanges();
    }, 0);
    return stored.handle;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (future.routeConfig !== curr.routeConfig) return false;
    if (this.isTabRoute(future)) return this.keyFromRoute(future) === this.keyFromRoute(curr);
    return true;
  }

  private destroyCached(key: string) {
    const stored = this.cache.get(key);
    if (stored) { this.ref(stored.handle)?.destroy(); this.cache.delete(key); }
  }

  private clearAll() { [...this.cache.keys()].forEach(k => this.destroyCached(k)); }

  // ---- scroll helpers ----
  private ref(handle: DetachedRouteHandle): ComponentRef<any> | undefined {
    return (handle as any)?.componentRef;
  }

  private captureScroll(handle: DetachedRouteHandle) {
    const res: Stored['scrolls'] = [];
    this.ref(handle)?.location?.nativeElement
      ?.querySelectorAll('*')
      .forEach((n: HTMLElement, idx: number) => {
        if (n.scrollTop || n.scrollLeft) res.push({ idx, top: n.scrollTop, left: n.scrollLeft });
      });
    return res;
  }

  private restoreScroll(stored: Stored) {
    const el: HTMLElement | undefined = this.ref(stored.handle)?.location?.nativeElement;
    if (!el) return;
    const nodes = el.querySelectorAll<HTMLElement>('*');
    stored.scrolls.forEach(s => {
      const n = nodes[s.idx];
      if (n) { n.scrollTop = s.top; n.scrollLeft = s.left; }
    });
  }
}